import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  QrCode, Clock, CheckCircle2, XCircle, RefreshCw, Copy, Check,
  ArrowLeft, Zap, Shield,
} from "lucide-react";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import StatusBadge from "@/shared/components/merchant/StatusBadge";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { createMerchantQRCode } from "@/shared/api/services/qrCodeService";


/* ─────────────────────────── STYLES ─────────────────────────── */
const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&family=Syne:wght@700;800&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700&display=swap');

.dep-root {
  font-family: 'DM Sans', sans-serif;
  min-height: 100vh;
  padding-bottom: 60px;
  --cyan: #22d3ee;
  --surface: rgba(8,16,38,0.96);
  --surface-2: rgba(11,22,50,0.95);
  --border: rgba(34,211,238,0.1);
  --border-hover: rgba(34,211,238,0.32);
  --text-1: #e8f4ff;
  --text-2: #7aaec8;
  --text-3: #3a5a78;
  --spring: cubic-bezier(0.34,1.56,0.64,1);
}
.dep-root * { box-sizing: border-box; }

/* ── Page title ── */
.dep-h1 {
  font-family: 'DM Sans', sans-serif;
  font-size: 1.85rem;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: #eaf6ff;
}
}
.dep-sub { color: var(--text-3); font-size: 13px; margin-top: 4px; font-weight: 500; }

/* ── Card ── */
.dep-card {
  background: linear-gradient(145deg, var(--surface), var(--surface-2));
  border: 1px solid var(--border);
  border-radius: 24px;
  padding: 28px;
  position: relative; overflow: hidden;
  transition: border-color 0.35s ease, box-shadow 0.35s ease;
}
.dep-card::before {
  content: '';
  position: absolute; top: 0; left: 15%; right: 15%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.38), transparent);
}
.dep-card::after {
  content: '';
  position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(ellipse 70% 40% at 50% -10%, rgba(34,211,238,0.04), transparent);
}
.dep-card:hover {
  border-color: rgba(34,211,238,0.2);
  box-shadow: 0 0 55px rgba(34,211,238,0.06), 0 24px 60px rgba(0,0,0,0.4);
}

/* ── Label ── */
.dep-label {
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #22d3ee;
  text-transform: uppercase;
}

/* ── Input ── */
.dep-input {
  width: 100%;
  background: rgba(34,211,238,0.04);
  border: 1px solid rgba(34,211,238,0.1);
  border-radius: 14px; padding: 13px 18px;
  color: var(--text-1); font-size: 15px; font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  outline: none;
  transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
  -moz-appearance: textfield;
}
.dep-input::-webkit-outer-spin-button,
.dep-input::-webkit-inner-spin-button { -webkit-appearance: none; }
.dep-input::placeholder { color: #1e3a50; }
.dep-input:focus {
  border-color: rgba(34,211,238,0.45);
  background: rgba(34,211,238,0.07);
  box-shadow: 0 0 0 4px rgba(34,211,238,0.08), 0 8px 32px rgba(0,0,0,0.4);
}
.dep-hint {
  font-family: 'JetBrains Mono', monospace;
  font-size: 15px;
  color: #5a8aaa;
  font-weight: 500;
  margin-top:5px;
}

/* ── Primary button ── */
.dep-btn-primary {
  width: 100%;
  background: linear-gradient(135deg, #06b6d4, #0891b2, #0e7490);
  border: none; border-radius: 16px; padding: 15px;
  color: #fff; font-size: 14px; font-weight: 700;
  cursor: pointer; letter-spacing: 0.02em;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  font-family: 'DM Sans', sans-serif;
  position: relative; overflow: hidden;
  transition: transform 0.25s var(--spring), box-shadow 0.25s, opacity 0.2s;
  box-shadow: 0 8px 32px rgba(6,182,212,0.38), inset 0 1px 0 rgba(255,255,255,0.15);
}
.dep-btn-primary::before {
  content: '';
  position: absolute; top: 0; left: -100%; width: 55%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.13), transparent);
  transform: skewX(-15deg); transition: left 0.55s;
}
.dep-btn-primary:hover:not(:disabled)::before { left: 160%; }
.dep-btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 14px 40px rgba(6,182,212,0.52), inset 0 1px 0 rgba(255,255,255,0.2);
}
.dep-btn-primary:active:not(:disabled) { transform: translateY(0) scale(0.97); }
.dep-btn-primary:disabled { opacity: 0.32; cursor: not-allowed; }

/* ── Back button ── */
.dep-btn-back {
  background: transparent; border: 1px solid rgba(34,211,238,0.14);
  border-radius: 12px; padding: 9px 16px;
  color: var(--text-3); font-size: 13px; font-weight: 600;
  cursor: pointer; display: inline-flex; align-items: center; gap: 8px;
  font-family: 'DM Sans', sans-serif; margin-bottom: 10px;
  transition: all 0.25s var(--spring);
}
.dep-btn-back:hover {
  border-color: rgba(34,211,238,0.38); color: var(--cyan);
  background: rgba(34,211,238,0.06); transform: translateX(-2px);
  box-shadow: 0 0 20px rgba(34,211,238,0.12);
}

/* ── Timer pill ── */
.dep-timer {
  display: inline-flex; align-items: center; gap: 9px;
  padding: 8px 22px; border-radius: 999px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 1rem; font-weight: 700; margin-bottom: 26px;
  border: 1px solid;
}

/* ── QR box ── */
.dep-qr-box {
  width: 220px; height: 220px; border-radius: 26px;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 36px; position: relative; overflow: hidden;
}
.dep-qr-badge {
  position: absolute; bottom: -13px; left: 50%; transform: translateX(-50%);
  background: linear-gradient(135deg, #06b6d4, #0891b2);
  color: #fff; font-size: 9px; font-weight: 800;
  padding: 4px 18px; border-radius: 999px; letter-spacing: 0.12em;
  box-shadow: 0 6px 22px rgba(6,182,212,0.5);
  text-transform: uppercase;
}

/* ── Amount display ── */
/* Amount Section */
.dep-amount-section {
  margin-top: 38px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.dep-amount-input {
  font-family: 'Orbitron', sans-serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: #fff;
  background-color: #0d1f35;
  border: 2px solid #22d3ee;
  border-radius: 8px;
  padding: 10px 14px;
  transition: all 0.3s ease;
  outline: none;
  text-align: right;
  box-shadow: 0 0 8px rgba(34,211,238,0.3);
}
.dep-amount-input:focus {
  border-color: #06b6d4;
  box-shadow: 0 0 10px rgba(34,211,238,0.6);
}
/* Premium Label */
.dep-amount-label {
  font-family: 'Orbitron', sans-serif; /* futuristic premium font */
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #22d3ee;
  text-transform: uppercase;
  text-shadow: 0 0 6px rgba(34,211,238,0.5), 0 0 12px rgba(34,211,238,0.3);
  transition: all 0.3s ease;
}

/* Amount + Dollar wrapper */
.dep-amount-wrapper {
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 10px;
}

/* Dollar Sign */
.dep-amount-prefix {
  font-family: 'Orbitron', sans-serif;
  font-size: 2.3rem;
  font-weight: 700;
  color: #22d3ee;
  text-shadow: 0 0 6px rgba(34,211,238,0.5), 0 0 12px rgba(34,211,238,0.3);
}

/* Amount Value */
.dep-amount-value {
  font-family: 'Orbitron', sans-serif;
  font-size: 2.8rem;
  font-weight: 800;
  color: #fff;
  background: linear-gradient(90deg, #22d3ee, #06b6d4, #0ea5e9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 10px rgba(34,211,238,0.45);
}

/* Copy button spacing */
.dep-copy-btn {
  margin-left: 6px;
}

/* Reference text */
.dep-amount-ref {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: #5a8aaa;
  font-weight: 500;
  span {
    font-family: 'Orbitron', sans-serif;
    font-weight: 700;
    color: #22d3ee;
    text-shadow: 0 0 6px rgba(34,211,238,0.4);
  }
}
  

/* ── Copy btn ── */
.dep-copy-btn {
  background: rgba(34,211,238,0.08); border: 1px solid rgba(34,211,238,0.18);
  border-radius: 11px; width: 38px; height: 38px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--text-3);
  transition: all 0.25s var(--spring);
}
.dep-copy-btn:hover { color: var(--cyan); background: rgba(34,211,238,0.14); transform: scale(1.12); box-shadow: 0 0 18px rgba(34,211,238,0.22); }
.dep-copy-btn:active { transform: scale(0.94); }

/* ── Pulse dot ── */
@keyframes pulseDot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.45)} }
.dep-pulse { width: 8px; height: 8px; border-radius: 50%; background: #f59e0b; animation: pulseDot 1.8s ease-in-out infinite; }

/* ── Icon rings ── */
.dep-icon-ring { width: 90px; height: 90px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; }
.dep-success-ring { background: radial-gradient(circle, rgba(16,185,129,0.2), rgba(16,185,129,0.05)); box-shadow: 0 0 45px rgba(16,185,129,0.28), 0 0 0 1px rgba(16,185,129,0.15); }
.dep-expired-ring { background: radial-gradient(circle, rgba(239,68,68,0.15), rgba(239,68,68,0.04)); box-shadow: 0 0 40px rgba(239,68,68,0.22), 0 0 0 1px rgba(239,68,68,0.12); }

/* ── Secondary btn ── */
.dep-btn-secondary {
  background: rgba(34,211,238,0.08); border: 1px solid rgba(34,211,238,0.35);
  border-radius: 14px; padding: 13px 28px; color: var(--cyan);
  font-size: 14px; font-weight: 700; cursor: pointer;
  display: inline-flex; align-items: center; gap: 9px;
  font-family: 'DM Sans', sans-serif;
  transition: all 0.25s var(--spring); position: relative; overflow: hidden;
}
.dep-btn-secondary:hover {
  background: rgba(34,211,238,0.14);
  box-shadow: 0 0 30px rgba(34,211,238,0.28); transform: translateY(-2px);
}
.dep-btn-secondary:active { transform: scale(0.97); }

/* ── Info box ── */
.dep-info {
  background: rgba(34,211,238,0.04); border: 1px solid rgba(34,211,238,0.1);
  border-radius: 18px; padding: 20px 22px; position: relative; overflow: hidden;
}
.dep-info::before {
  content: '';
  position: absolute; top: 0; left: 20%; right: 20%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.28), transparent);
}

/* ── Status note ── */
.dep-status-note {
  background: rgba(34,211,238,0.04); border: 1px solid rgba(34,211,238,0.1);
  border-radius: 14px; padding: 12px 18px;
  text-align: center; color: var(--text-3); font-size: 12px; font-weight: 500;
}

/* ── Step dots ── */
.dep-step-wrap { display: flex; gap: 6px; justify-content: center; margin-bottom: 22px; }

/* ── Spin ── */
@keyframes spin { to { transform: rotate(360deg); } }
.dep-spin { animation: spin 0.85s linear infinite; }

/* ── Quick presets ── */
.dep-preset-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; margin-top: 12px; }
.dep-preset-btn {
  background: rgba(34,211,238,0.04); border: 1px solid rgba(34,211,238,0.1);
  border-radius: 11px; padding: 9px; text-align: center;
  color: var(--text-3); font-size: 13px; font-weight: 600;
  font-family: 'JetBrains Mono', monospace; cursor: pointer;
  transition: all 0.25s var(--spring);
}
.dep-preset-btn:hover {
  border-color: rgba(34,211,238,0.35); color: var(--cyan);
  background: rgba(34,211,238,0.09); transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(34,211,238,0.12);
}
.dep-preset-btn:active { transform: scale(0.95); }

/* ── Responsive ── */
@media (max-width: 480px) {
  .dep-amount { font-size: 2rem; }
  .dep-qr-box { width: 190px; height: 190px; }
  .dep-card { padding: 20px; border-radius: 18px; }
  .dep-preset-grid { grid-template-columns: repeat(3,1fr); }
}
`;

/* ─────────────── AMOUNT INPUT ─────────────── */
const AmountInput = ({ value, onChange, onPreset }) => {
  const [focused, setFocused] = useState(false);
  const presets = [500, 1000, 2000, 5000, 10000, 50000];
  return (
    <div>
      <div style={{ position: "relative" }}>
        <motion.span
          animate={{ color: focused ? "#22d3ee" : value ? "#7aaec8" : "#1e3a50" }}
          style={{
            position: "absolute", left: 18, top: "50%", transform: "translateY(-50%)",
            fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
            pointerEvents: "none", zIndex: 2,
          }}
        >฿</motion.span>
        <input
          className="dep-input"
          type="number"
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="0.00"
          min="20" max="2000000"
          style={{ paddingLeft: 46, fontSize: "1.3rem", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}
        />
        <motion.div
          animate={{ scaleX: focused ? 1 : 0, opacity: focused ? 1 : 0 }}
          initial={{ scaleX: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          style={{
            position: "absolute", bottom: -1, left: 12, right: 12, height: 2,
            borderRadius: 99, transformOrigin: "left",
            background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.6), transparent)",
          }}
        />
      </div>
      <div className="dep-preset-grid">
        {presets.map(amt => (
          <motion.button
            key={amt} className="dep-preset-btn"
            onClick={() => onPreset(String(amt))}
            whileTap={{ scale: 0.93 }}
          >
            {amt >= 1000 ? `฿${amt / 1000}K` : `฿${amt}`}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

/* ─────────────── STEP DOTS ─────────────── */
const StepDots = ({ step }) => {
  const steps = ["form", "qr", "success"];
  const idx = steps.indexOf(step);
  return (
    <div className="dep-step-wrap">
      {steps.map((s, i) => (
        <motion.div
          key={s}
          animate={{
            width: i === idx ? 26 : 6,
            background: i === idx ? "#22d3ee" : i < idx ? "rgba(34,211,238,0.4)" : "rgba(34,211,238,0.1)",
            boxShadow: i === idx ? "0 0 14px rgba(34,211,238,0.55)" : "none",
          }}
          transition={{ type: "spring", stiffness: 500, damping: 38 }}
          style={{ height: 6, borderRadius: 99 }}
        />
      ))}
    </div>
  );
};

/* ─────────────── MAIN ─────────────── */
export default function MerchantDeposit() {
  const { t } = useLanguage();
  const [step, setStep] = useState("form");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [qrPayload, setQrPayload] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(900);
  const [copied, setCopied] = useState(false);
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (step === "qr" && timeLeft > 0) {
      const timer = setInterval(() => setTimeLeft(p => {
        if (p <= 1) { setStep("expired"); return 0; }
        return p - 1;
      }), 1000);
      return () => clearInterval(timer);
    }
  }, [step, timeLeft]);

  useEffect(() => {
    if (step === "success") {
      setParticles(Array.from({ length: 22 }, (_, i) => ({
        id: i, x: Math.random() * 100, delay: Math.random() * 0.5,
        duration: 1.5 + Math.random(), size: 4 + Math.random() * 6,
        color: ["#22d3ee", "#10b981", "#3b82f6", "#a78bfa", "#f59e0b"][Math.floor(Math.random() * 5)],
      })));
    }
  }, [step]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
  const isTimerDanger = timeLeft < 60;
  const timerPercent = (timeLeft / 900) * 100;

  const handleGenerate = async () => {
    if (!amount || parseFloat(amount) < 20) return;

    setErrorMessage("");
    setLoading(true);

    try {
      const createdQr = await createMerchantQRCode({
        amount,
        reference,
      });

      const expiresAt = createdQr?.expires_at ? new Date(createdQr.expires_at) : null;
      const nextTimeLeft = expiresAt
        ? Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000))
        : 900;

      setQrPayload(createdQr);
      setTimeLeft(nextTimeLeft || 900);
      setStep("qr");
    } catch (error) {
      setErrorMessage(error?.message || "Failed to generate QR code");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep("form");
    setAmount("");
    setReference("");
    setQrPayload(null);
    setErrorMessage("");
    setTimeLeft(900);
  };
  const copyAmount = () => {
    navigator.clipboard.writeText(amount);
    setCopied(true); setTimeout(() => setCopied(false), 2200);
  };

  const canSubmit = !loading && amount && parseFloat(amount) >= 20;

  return (
    <MerchantLayout currentPage="MerchantDeposit" merchantName="Royal Gaming" merchantCode="MRC001">
      <style>{STYLE}</style>

      <div className="dep-root">
        <div style={{ maxWidth: 500, margin: "0 auto" }}>

          {step !== "expired" && <StepDots step={step} />}

          <AnimatePresence mode="wait">

            {/* ══ FORM ══ */}
            {step === "form" && (
              <motion.div key="form"
                initial={{ opacity: 0, y: 26, filter: "blur(6px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -22, filter: "blur(6px)", scale: 0.97 }}
                transition={{ duration: 0.48, ease: [0.16, 1, 0.3, 1] }}
                style={{ display: "flex", flexDirection: "column", gap: 18 }}
              >
                {/* Header */}
                <motion.div
                  initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.4 }}
                  style={{ display: "flex", alignItems: "center", gap: 14 }}
                >
                  <div style={{
                    width: 48, height: 48, borderRadius: 16, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                    boxShadow: "0 10px 28px rgba(6,182,212,0.45)",
                    position: "relative", overflow: "hidden",
                  }}>
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(145deg,rgba(255,255,255,0.15),transparent)" }} />
                    <QrCode size={22} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
                  </div>
                  <div>
                    <h1 className="dep-h1">{t("deposits")} · QR</h1>
                    <p className="dep-sub">PromptPay QR Code Generator</p>
                  </div>
                </motion.div>

                {/* Main card */}
                <motion.div className="dep-card"
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 }}
                  style={{ display: "flex", flexDirection: "column", gap: 20 }}
                >
                  <div className="dep-amount-input-section">
  {/* Label */}
  <label className="dep-label">{t("amountThb")} *</label>

  {/* Amount Input */}
  <AmountInput
    className="dep-amount-input"
    value={amount}
    onChange={(e) => setAmount(e.target.value)}
    onPreset={(val) => setAmount(val)}
  />

  {/* Hint Text */}
  <p className="dep-hint">Min: ฿20 · Max: ฿2,000,000</p>
</div>

                  <div>
                    <label className="dep-label">
                      {t("reference")} <span style={{ color: "#1e3a50", textTransform: "none", letterSpacing: 0 }}>({t("optionalCustomerRef")})</span>
                    </label>
                    <input
                      className="dep-input"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder={t("orderIdPlaceholder")}
                    />
                  </div>

                  <motion.button
                    className="dep-btn-primary"
                    onClick={handleGenerate}
                    disabled={!canSubmit}
                    whileTap={canSubmit ? { scale: 0.97 } : {}}
                  >
                    {loading ? (
                      <>
                        <div className="dep-spin"><RefreshCw size={17} /></div>
                        {t("creating")}…
                      </>
                    ) : (
                      <>
                        <QrCode size={17} />
                        {t("create")} QR Code
                      </>
                    )}
                  </motion.button>

                  {!!errorMessage && (
                    <div className="dep-status-note" style={{ borderColor: "rgba(239,68,68,0.3)", color: "#f87171" }}>
                      {errorMessage}
                    </div>
                  )}
                </motion.div>

                {/* Info card */}
                <motion.div className="dep-info"
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.22 }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <Zap size={14} style={{ color: "#22d3ee" }} />
                    <h4 style={{ color: "#22d3ee", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                      How it works
                    </h4>
                  </div>
                  {[
                    "Enter deposit amount & optional reference",
                    "Generate the PromptPay QR code",
                    "Customer scans & pays via banking app",
                    "Payment confirmed automatically",
                  ].map((text, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.28 + i * 0.06 }}
                      style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: i < 3 ? 10 : 0 }}
                    >
                      <div style={{
                        width: 20, height: 20, borderRadius: 7, flexShrink: 0, marginTop: 1,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: "rgba(34,211,238,0.1)", border: "1px solid rgba(34,211,238,0.2)",
                        fontSize: 10, fontWeight: 800, color: "#22d3ee",
                      }}>{i + 1}</div>
                      <p style={{ color: "#2a5070", fontSize: 12.5, fontWeight: 500, lineHeight: 1.55 }}>{text}</p>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            )}

            {/* ══ QR ══ */}
            {step === "qr" && (
              <motion.div key="qr"
                initial={{ opacity: 0, y: 26, filter: "blur(6px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -22, filter: "blur(6px)", scale: 0.97 }}
                transition={{ duration: 0.48, ease: [0.16, 1, 0.3, 1] }}
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <motion.button className="dep-btn-back" onClick={handleReset} whileTap={{ scale: 0.96 }}>
                  <ArrowLeft size={14} /> Back
                </motion.button>

                <div className="dep-card" style={{ textAlign: "center" }}>
                  {/* Timer */}
                  <motion.div
                    className="dep-timer"
                    animate={{
                      background: isTimerDanger ? "rgba(239,68,68,0.12)" : "rgba(245,158,11,0.07)",
                      color: isTimerDanger ? "#f87171" : "#fbbf24",
                      borderColor: isTimerDanger ? "rgba(239,68,68,0.28)" : "rgba(245,158,11,0.2)",
                      scale: isTimerDanger ? [1, 1.02, 1] : 1,
                    }}
                    transition={{ scale: { repeat: Infinity, duration: 1 } }}
                  >
                    <Clock size={15} />
                    {formatTime(timeLeft)}
                  </motion.div>

                  {/* Timer bar */}
                  <div style={{ height: 3, background: "rgba(255,255,255,0.04)", borderRadius: 99, marginBottom: 30, overflow: "hidden" }}>
                    <motion.div
                      animate={{ width: `${timerPercent}%` }}
                      style={{
                        height: "100%", borderRadius: 99,
                        background: isTimerDanger
                          ? "linear-gradient(90deg, #ef4444, #f87171)"
                          : "linear-gradient(90deg, #06b6d4, #22d3ee)",
                        boxShadow: isTimerDanger ? "0 0 10px rgba(239,68,68,0.6)" : "0 0 10px rgba(34,211,238,0.55)",
                      }}
                      transition={{ duration: 1, ease: "linear" }}
                    />
                  </div>

                  {/* QR Box */}
                  <motion.div
                    className="dep-qr-box"
                    style={{ background: "linear-gradient(145deg, #0891b2, #06b6d4, #0ea5e9)" }}
                    animate={{ boxShadow: ["0 0 40px rgba(6,182,212,0.38)", "0 0 75px rgba(6,182,212,0.58)", "0 0 40px rgba(6,182,212,0.38)"] }}
                    transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
                    whileHover={{ scale: 1.035 }}
                  >
                    <QrCode size={104} style={{ color: "rgba(255,255,255,0.92)", position: "relative", zIndex: 1 }} />
                    <div style={{ position: "absolute", inset: 0, borderRadius: 26, background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.13), transparent 60%)" }} />
                    <div className="dep-qr-badge">PROMPTPAY</div>
                  </motion.div>

                  {/* Amount */}
                  {/* Amount */}
<div className="dep-amount-section">
  <p className="dep-amount-label">Amount Due</p>

  <div className="dep-amount-wrapper">
    {/* Dollar Sign */}
    <motion.span
      className="dep-amount-prefix"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 500, damping: 26 }}
    >
      ฿
    </motion.span>

    {/* Amount */}
    <motion.span
      className="dep-amount-value"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 500, damping: 26 }}
    >
      {parseFloat(amount).toLocaleString()}
    </motion.span>

    {/* Copy Button */}
    <motion.button className="dep-copy-btn" onClick={copyAmount} whileTap={{ scale: 0.9 }}>
      <AnimatePresence mode="wait">
        {copied
          ? <motion.div key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
              <Check size={16} style={{ color: "#10b981" }} />
            </motion.div>
          : <motion.div key="copy" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
              <Copy size={16} />
            </motion.div>
        }
      </AnimatePresence>
    </motion.button>
  </div>

  {/* Optional Reference */}
  {reference && (
    <p className="dep-amount-ref">
      Ref: <span>{reference}</span>
    </p>
  )}
</div>

                  {/* Pending pulse */}
                  <motion.div
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 24 }}
                    animate={{ opacity: [0.65, 1, 0.65] }}
                    transition={{ duration: 2.5, repeat: Infinity }}
                  >
                    <div className="dep-pulse" />
                    <span style={{ color: "#5a8aaa", fontSize: 13, fontWeight: 600 }}>
                      Awaiting payment{qrPayload?.status ? ` (${String(qrPayload.status).toUpperCase()})` : ""}...
                    </span>
                  </motion.div>

                  {!!qrPayload?.payment_id && (
                    <div style={{ marginTop: 10, color: "#5a8aaa", fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
                      Payment ID: {qrPayload.payment_id}
                    </div>
                  )}
                </div>

                <div className="dep-status-note">
                  <Shield size={12} style={{ display: "inline", marginRight: 6, color: "#22d3ee" }} />
                  Secured · Auto-refreshing status every 3 seconds
                </div>
              </motion.div>
            )}

            {/* ══ SUCCESS ══ */}
            {step === "success" && (
              <motion.div key="success"
                initial={{ opacity: 0, scale: 0.88, filter: "blur(8px)" }}
                animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                style={{ position: "relative" }}
              >
                {/* Confetti particles */}
                {particles.map(p => (
                  <motion.div key={p.id}
                    initial={{ y: 0, x: `${p.x}vw`, opacity: 1, scale: 0 }}
                    animate={{ y: -220, opacity: 0, scale: 1, rotate: Math.random() * 360 }}
                    transition={{ delay: p.delay, duration: p.duration, ease: "easeOut" }}
                    style={{
                      position: "absolute", width: p.size, height: p.size,
                      borderRadius: Math.random() > 0.5 ? "50%" : 3,
                      background: p.color, pointerEvents: "none", zIndex: 10,
                      boxShadow: `0 0 9px ${p.color}`,
                    }}
                  />
                ))}

                <div className="dep-card" style={{ textAlign: "center" }}>
                  <motion.div
                    className="dep-icon-ring dep-success-ring"
                    initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.1, type: "spring", stiffness: 400, damping: 20 }}
                  >
                    <CheckCircle2 size={44} style={{ color: "#10b981" }} />
                  </motion.div>

                  <motion.h2
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    style={{ color: "#e8f4ff", fontSize: "1.65rem", fontWeight: 800, marginBottom: 8, fontFamily: "'Syne', sans-serif" }}
                  >
                    Payment Received!
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.32 }}
                    style={{ color: "#3a5a78", fontSize: 14, marginBottom: 28, fontWeight: 500 }}
                  >
                    ฿{parseFloat(amount).toLocaleString()} has been successfully deposited.
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.38 }}
                    style={{
                      background: "rgba(16,185,129,0.07)", border: "1px solid rgba(16,185,129,0.2)",
                      borderRadius: 16, padding: "18px", marginBottom: 28,
                    }}
                  >
                    <StatusBadge status="success" />
                    <p style={{ color: "#2a5040", fontSize: 11, marginTop: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                      TXN{Date.now().toString().slice(-10)}
                    </p>
                  </motion.div>

                  <motion.button
                    className="dep-btn-secondary"
                    onClick={handleReset}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    <QrCode size={16} /> New QR Code
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* ══ EXPIRED ══ */}
            {step === "expired" && (
              <motion.div key="expired"
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="dep-card" style={{ textAlign: "center" }}>
                  <motion.div
                    className="dep-icon-ring dep-expired-ring"
                    initial={{ scale: 0 }} animate={{ scale: 1 }}
                    transition={{ delay: 0.1, type: "spring", stiffness: 400, damping: 20 }}
                  >
                    <XCircle size={44} style={{ color: "#ef4444" }} />
                  </motion.div>

                  <h2 style={{ color: "#e8f4ff", fontSize: "1.5rem", fontWeight: 800, marginBottom: 8, fontFamily: "'Syne', sans-serif" }}>
                    QR Code Expired
                  </h2>
                  <p style={{ color: "#3a5a78", fontSize: 13.5, marginBottom: 28, fontWeight: 500 }}>
                    The payment window has closed. Please generate a new QR code.
                  </p>

                  <motion.button
                    className="dep-btn-primary"
                    style={{ display: "inline-flex", width: "auto", padding: "13px 34px" }}
                    onClick={handleReset}
                    whileTap={{ scale: 0.97 }}
                  >
                    <RefreshCw size={16} /> Try Again
                  </motion.button>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </MerchantLayout>
  );
}